import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import StudentRepository from '../services/StudentRepository.js';
import {
  Box,
  Button,
  List,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputLabel,
  FormControl,
  Grid,
  Alert,
} from '@mui/material';
import FormButtonGroup from '../components/layout/FormButtonGroup.jsx';
import OperationModal from '../components/common/OperationModal.jsx';
import EditableList from '../components/common/EditableList.jsx';
import DeleteDialog from '../components/common/DeleteDialog.jsx';
import { formatDateRange } from '../utils/dateFormatter.js';
import { capitalize } from '../utils/stringUtils.js';

export default function Employment({
  repo,
  handleScreenNext,
  handleScreenBack,
  handleErrors,
  formValues,
  setFormValues,
  onSkip,
}) {
  const defaultEmploymentValues = {
    title: null,
    status: '',
    level: '',
    company: null,
    org_type: '',
    location: null,
    monthly_income: '',
    start_month: '',
    start_year: null,
    end_month: '',
    end_year: null,
  };
  const defaultCareerBreakValues = {
    reason: '',
    location: null,
    start_month: '',
    start_year: null,
    end_month: '',
    end_year: null,
    fs_course: null,
    fs_school: null,
  };
  const defaultDeleteValues = {
    id: 0,
    name: null,
  };
  /**
   * STATES
   */
  const [loading, setLoading] = React.useState(false);
  const [employmentFormValues, setEmploymentFormValues] = React.useState(defaultEmploymentValues);
  const [careerBreakValues, setCareerBreakFormValues] = React.useState(defaultCareerBreakValues);
  const [deleteValues, setDeleteValues] = React.useState(defaultDeleteValues);
  const [operation, setOperation] = React.useState('addEmployment');
  const [recordId, setRecordId] = React.useState(null);
  const [isEmpModalOpen, setIsEmpModalOpen] = React.useState(false);
  const [isPresentRecord, setIsPresentRecord] = React.useState(false);
  const [isCareerBreakModalOpen, setIsCareerBreakModalOpen] = React.useState(false);
  const [isNotYetEmployedModalOpen, setIsNotYetEmployedModalOpen] = React.useState(false);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [isDialogOperationLoading, setIsDialogOperationLoading] = React.useState(false);
  const [employmentRecords, setEmploymentRecords] = React.useState([]);
  /**
   * FUNCTIONS
   */
  const doOperation = () => {
    if (operation == 'updateEmployment')
      return repo.updateEmploymentRecord(recordId, employmentFormValues);
    if (operation == 'updateCareerBreak')
      return repo.updateCareerBreakRecord(recordId, careerBreakValues);
    if (operation == 'addCareerBreak' || operation == 'addNotYetEmployedBreak')
      return repo.addCareerBreakRecord(careerBreakValues);
    return repo.addEmploymentRecord(employmentFormValues);
  };
  const handleEmpFormValueChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    setEmploymentFormValues({
      ...employmentFormValues,
      [name]: value,
    });
  };
  const handleBreakFormValueChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    setCareerBreakFormValues({
      ...careerBreakValues,
      [name]: value,
    });
  };
  const handleValueChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };
  const handleNext = async () => {
    setLoading(true);
    repo
      .updateMonthsBeforeFirstJob(formValues.months_before_first_job)
      .then(() => {
        setLoading(false);
        handleScreenNext();
      })
      .catch(() => {
        setLoading(false);
        handleErrors(
          <Alert variant="filled" severity="error" sx={{ marginBottom: 3 }}>
            <Typography variant="caption">
              We are unable to submit your answers. Please contact the administrator at
              webteam@msuiit.edu.ph.
            </Typography>
          </Alert>
        );
      });
  };
  const isShiftable = () => {
    return formValues.months_before_first_job && !loading;
  };
  const handleDialogClose = () => {
    setEmploymentFormValues(defaultEmploymentValues);
    setCareerBreakFormValues(defaultCareerBreakValues);
    setIsDialogOperationLoading(false);
    setIsDialogOpen(false);
    setDeleteValues(defaultDeleteValues);
  };
  const handleCheckboxChange = (event) => {
    setIsPresentRecord(event.target.checked);
    if (event.target.checked) {
      setEmploymentFormValues({
        ...employmentFormValues,
        ...{
          end_month: '',
          end_year: null,
        },
      });
      setCareerBreakFormValues({
        ...careerBreakValues,
        ...{
          end_month: '',
          end_year: null,
        },
      });
    }
  };
  const updateEmploymentListDisplay = () => {
    repo.getEmploymentRecords().then((data) => {
      setEmploymentRecords(data);
    });
  };
  const openAddEmploymentItem = () => {
    setOperation('addEmployment');
    setIsEmpModalOpen(true);
  };
  const openAddCareerBreakItem = () => {
    setOperation('addCareerBreak');
    setIsCareerBreakModalOpen(true);
  };
  const openNotYetEmployedModal = () => {
    setOperation('addNotYetEmployedBreak');
    setIsNotYetEmployedModalOpen(true);
  };
  const openEditEmploymentItem = (id, employmentRecordDetails) => {
    setOperation('updateEmployment');
    setEmploymentFormValues({
      title: employmentRecordDetails.title,
      status: employmentRecordDetails.status,
      level: employmentRecordDetails.level,
      company: employmentRecordDetails.company,
      org_type: employmentRecordDetails.org_type,
      location: employmentRecordDetails.location,
      monthly_income: employmentRecordDetails.monthly_income,
      start_month: employmentRecordDetails.start_month,
      start_year: employmentRecordDetails.start_year,
      end_month: employmentRecordDetails.end_month,
      end_year: employmentRecordDetails.end_year,
    });
    setIsPresentRecord(
      employmentRecordDetails.end_year === null || employmentRecordDetails.end_year === ''
    );
    setRecordId(id);
    setIsEmpModalOpen(true);
  };
  const openEditCareerBreakItem = (id, careerBreakRecordDetails) => {
    setOperation('updateCareerBreak');
    setCareerBreakFormValues({
      reason: careerBreakRecordDetails.reason,
      location: careerBreakRecordDetails.location,
      start_month: careerBreakRecordDetails.start_month,
      start_year: careerBreakRecordDetails.start_year,
      end_month: careerBreakRecordDetails.end_month,
      end_year: careerBreakRecordDetails.end_year,
      fs_course: careerBreakRecordDetails.fs_course,
      fs_school: careerBreakRecordDetails.fs_school,
    });
    setIsPresentRecord(
      careerBreakRecordDetails.end_year === null || careerBreakRecordDetails.end_year === ''
    );
    setRecordId(id);
    setIsCareerBreakModalOpen(true);
  };
  const openDeleteEmploymentItem = (id, name) => {
    setOperation('deleteEmployment');
    setDeleteValues({
      id,
      name,
    });
    setIsDialogOpen(true);
  };
  const openDeleteCareerBreakItem = (id, name) => {
    setOperation('deleteCareerBreak');
    setDeleteValues({
      id,
      name,
    });
    setIsDialogOpen(true);
  };
  const handleDeleteItem = () => {
    setIsDialogOperationLoading(true);
    if (operation == 'deleteCareerBreak')
      return repo.deleteCareerBreak(deleteValues.id).then(() => {
        setIsDialogOperationLoading(false);
        setIsDialogOpen(false);
        setCareerBreakFormValues(defaultCareerBreakValues);
        updateEmploymentListDisplay();
      });
    return repo.deleteEmploymentRecord(deleteValues.id).then(() => {
      setIsDialogOperationLoading(false);
      setIsDialogOpen(false);
      setEmploymentFormValues(defaultEmploymentValues);
      updateEmploymentListDisplay();
    });
  };
  const canCloseEmploymentModal = () => {
    if (
      !employmentFormValues.title ||
      !employmentFormValues.status ||
      !employmentFormValues.level ||
      !employmentFormValues.company ||
      !employmentFormValues.org_type ||
      !employmentFormValues.location ||
      !employmentFormValues.monthly_income ||
      !employmentFormValues.start_month ||
      !employmentFormValues.start_year ||
      (!isPresentRecord && (!employmentFormValues.end_month || !employmentFormValues.end_year))
    )
      return false;
    if (!isPresentRecord && (!employmentFormValues.end_month || !employmentFormValues.end_year))
      return false;
    return !loading;
  };
  const canCloseCareerBreakModal = () => {
    if (
      !careerBreakValues.reason ||
      !careerBreakValues.location ||
      !careerBreakValues.start_month ||
      !careerBreakValues.start_year
    )
      return false;

    if (!isPresentRecord && (!careerBreakValues.end_month || !careerBreakValues.end_year))
      return false;
    if (
      careerBreakValues.reason === 'advanced-study' &&
      (!careerBreakValues.fs_school || !careerBreakValues.fs_course)
    )
      return false;
    return !loading;
  };
  useEffect(() => {
    updateEmploymentListDisplay();
  }, []);
  /**
   * RENDER
   */
  return (
    <React.Fragment>
      <OperationModal
        maxWidth={'md'}
        open={isEmpModalOpen}
        setIsModalOpen={setIsEmpModalOpen}
        onSubmit={updateEmploymentListDisplay}
        canSubmit={canCloseEmploymentModal(employmentFormValues)}
        employmentFormValues={employmentFormValues}
        defaultValues={defaultEmploymentValues}
        setFormValues={setEmploymentFormValues}
        operation={doOperation}
      >
        <Grid
          container
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          gap="30px"
        >
          <Grid
            item
            xs={12}
            md={5}
            display="flex"
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={'10px'}
          >
            <TextField
              required
              className="subvariant-nobreakpoint"
              id="title"
              name="title"
              label="Job Title"
              variant="outlined"
              onChange={handleEmpFormValueChange}
              value={employmentFormValues.title ? employmentFormValues.title : ''}
              sx={{ minWidth: 0 }}
              fullWidth
            />
            <FormControl required fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                className="subvariant-nobreakpoint"
                labelId="status-label"
                id="status"
                value={employmentFormValues.status}
                label="Status"
                name="status"
                onChange={handleEmpFormValueChange}
              >
                <MenuItem value="regular">Regular or Permanent</MenuItem>
                <MenuItem value="temporary">Temporary</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="contractual">Contractual</MenuItem>
                <MenuItem value="self-employed">Self-employed</MenuItem>
                <MenuItem value="unemployed">Unemployed</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl required fullWidth>
              <InputLabel id="level-label">Level</InputLabel>
              <Select
                className="subvariant-nobreakpoint"
                labelId="level-label"
                id="level"
                value={employmentFormValues.level}
                label="Level"
                name="level"
                onChange={handleEmpFormValueChange}
              >
                <MenuItem value="worker">Worker/Entry Level</MenuItem>
                <MenuItem value="mid-level">Mid-Level Management</MenuItem>
                <MenuItem value="senior">Senior/Top Management</MenuItem>
              </Select>
            </FormControl>
            <TextField
              required
              id="company"
              className="subvariant-nobreakpoint"
              name="company"
              label="Company Name"
              variant="outlined"
              onChange={handleEmpFormValueChange}
              value={employmentFormValues.company ? employmentFormValues.company : ''}
              fullWidth
            />
            <FormControl required fullWidth>
              <InputLabel id="org-type-label">Organization Type</InputLabel>
              <Select
                labelId="org-type-label"
                id="org-type"
                className="subvariant-nobreakpoint"
                name="org_type"
                value={employmentFormValues.org_type}
                label="Type of Organization"
                onChange={handleEmpFormValueChange}
              >
                <MenuItem value="private">Private</MenuItem>
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="ngo">NGO</MenuItem>
                <MenuItem value="non-profit">Non-profit</MenuItem>
                <MenuItem value="not-applicable">Not Applicable</MenuItem>
              </Select>
            </FormControl>
            <TextField
              required
              className="subvariant-nobreakpoint"
              id="location"
              name="location"
              label="Location"
              variant="outlined"
              onChange={handleEmpFormValueChange}
              value={employmentFormValues.location ? employmentFormValues.location : ''}
              fullWidth
            />
            <FormControl required fullWidth>
              <InputLabel id="monthly-income-label">Gross Monthly Earning</InputLabel>
              <Select
                labelId="monthly-income-label"
                className="subvariant-nobreakpoint"
                id="monthly-income"
                name="monthly_income"
                value={employmentFormValues.monthly_income}
                label="Gross Monthly Earning"
                onChange={handleEmpFormValueChange}
              >
                <MenuItem value="< ₱5000">Below ₱5000</MenuItem>
                <MenuItem value="₱5000 - ₱6999">₱5000 to less than ₱7000</MenuItem>
                <MenuItem value="₱7000 - ₱8999">₱7000 to less than ₱9000</MenuItem>
                <MenuItem value="₱9000 - ₱10999">₱9000 to less than ₱10000</MenuItem>
                <MenuItem value="₱11000 - ₱12999">₱11000 to less than ₱13000</MenuItem>
                <MenuItem value="₱13000 - ₱14999">₱13000 to less than ₱15000</MenuItem>
                <MenuItem value="₱15000 - ₱19999">₱15000 to less than ₱20000</MenuItem>
                <MenuItem value="₱20000 - ₱24999">₱20000 to less than ₱25000</MenuItem>
                <MenuItem value="₱25000 - ₱29999">₱25000 to less than ₱30000</MenuItem>
                <MenuItem value="₱30000 - ₱34999">₱30000 to less than ₱35000</MenuItem>
                <MenuItem value="₱35000 - ₱39999">₱35000 to less than ₱40000</MenuItem>
                <MenuItem value="₱40000 - ₱44999">₱40000 to less than ₱45000</MenuItem>
                <MenuItem value="₱45000 - ₱49999">₱45000 to less than ₱50000</MenuItem>
                <MenuItem value="> ₱50000">₱50000 and above</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            md={5}
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={'10px'}
          >
            <Typography variant="formTitle" color={'rgba(0, 0, 0, 0.6)'}>
              Start Date
            </Typography>
            <FormControl required fullWidth>
              <InputLabel id="start-month-label">Start Month</InputLabel>
              <Select
                className="subvariant-nobreakpoint"
                labelId="start-month-label"
                id="start-month"
                name="start_month"
                value={employmentFormValues.start_month}
                label="Start Month"
                onChange={handleEmpFormValueChange}
              >
                <MenuItem value="1">January</MenuItem>
                <MenuItem value="2">February</MenuItem>
                <MenuItem value="3">March</MenuItem>
                <MenuItem value="4">April</MenuItem>
                <MenuItem value="5">May</MenuItem>
                <MenuItem value="6">June</MenuItem>
                <MenuItem value="7">July</MenuItem>
                <MenuItem value="8">August</MenuItem>
                <MenuItem value="9">September</MenuItem>
                <MenuItem value="10">October</MenuItem>
                <MenuItem value="11">November</MenuItem>
                <MenuItem value="12">December</MenuItem>
              </Select>
            </FormControl>
            <TextField
              className="subvariant-nobreakpoint"
              required
              id="start_year"
              type="number"
              name="start_year"
              label="Start Year"
              variant="outlined"
              onChange={handleEmpFormValueChange}
              value={employmentFormValues.start_year ? employmentFormValues.start_year : ''}
              fullWidth
            />
            <FormControlLabel
              control={<Checkbox checked={isPresentRecord} onChange={handleCheckboxChange} />}
              label="I am currently employed in this"
              size="small"
            />
            <FormControl fullWidth>
              <InputLabel id="end-month-label">End Month</InputLabel>
              <Select
                className="subvariant-nobreakpoint"
                labelId="end-month-label"
                id="end-month"
                name="end_month"
                value={employmentFormValues.end_month}
                disabled={isPresentRecord}
                label="End Month"
                onChange={handleEmpFormValueChange}
              >
                <MenuItem value="1">January</MenuItem>
                <MenuItem value="2">February</MenuItem>
                <MenuItem value="3">March</MenuItem>
                <MenuItem value="4">April</MenuItem>
                <MenuItem value="5">May</MenuItem>
                <MenuItem value="6">June</MenuItem>
                <MenuItem value="7">July</MenuItem>
                <MenuItem value="8">August</MenuItem>
                <MenuItem value="9">September</MenuItem>
                <MenuItem value="10">October</MenuItem>
                <MenuItem value="11">November</MenuItem>
                <MenuItem value="12">December</MenuItem>
              </Select>
            </FormControl>
            <TextField
              id="end_year"
              type="number"
              className="subvariant-nobreakpoint"
              name="end_year"
              label="End Year"
              variant="outlined"
              disabled={isPresentRecord}
              onChange={handleEmpFormValueChange}
              value={employmentFormValues.end_year ? employmentFormValues.end_year : ''}
              fullWidth
            />
          </Grid>
        </Grid>
      </OperationModal>
      <OperationModal
        open={isCareerBreakModalOpen}
        setIsModalOpen={setIsCareerBreakModalOpen}
        onSubmit={updateEmploymentListDisplay}
        canSubmit={canCloseCareerBreakModal(careerBreakValues)}
        formValues={careerBreakValues}
        defaultValues={defaultCareerBreakValues}
        setFormValues={setCareerBreakFormValues}
        operation={doOperation}
      >
        <FormControl required>
          <InputLabel id="reason-label">Reason</InputLabel>
          <Select
            labelId="reason-label"
            id="reason"
            name="reason"
            value={careerBreakValues.reason}
            label="Reason"
            onChange={handleBreakFormValueChange}
          >
            <MenuItem value="advanced-study">Advanced or further study</MenuItem>
            <MenuItem value="exam-prep">Board/Licensure Examination Preparation</MenuItem>
            <MenuItem value="family-related">Family-related reasons</MenuItem>
            <MenuItem value="health-related">Health-related reasons</MenuItem>
            <MenuItem value="apprentice-work">Apprenticeship/Volunteer work</MenuItem>
            <MenuItem value="no-job-opportunity">No job opportunity</MenuItem>
            <MenuItem value="externship">Externship</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <TextField
          required
          id="location"
          name="location"
          label="Location"
          variant="outlined"
          onChange={handleBreakFormValueChange}
          value={careerBreakValues.location ? careerBreakValues.location : ''}
          fullWidth
        />
        {careerBreakValues.reason == 'advanced-study' && (
          <React.Fragment>
            <TextField
              required
              id="fs_course"
              name="fs_course"
              label="Course"
              variant="outlined"
              onChange={handleBreakFormValueChange}
              value={careerBreakValues.fs_course ? careerBreakValues.fs_course : ''}
              fullWidth
              margin="dense"
            />
            <TextField
              required
              id="fs_school"
              name="fs_school"
              label="School"
              variant="outlined"
              onChange={handleBreakFormValueChange}
              value={careerBreakValues.fs_school ? careerBreakValues.fs_school : ''}
              fullWidth
              margin="dense"
            />
          </React.Fragment>
        )}
        <FormControl required>
          <InputLabel id="start-month-label">Start Month</InputLabel>
          <Select
            labelId="start-month-label"
            id="start-month"
            name="start_month"
            value={careerBreakValues.start_month}
            label="Start Month"
            onChange={handleBreakFormValueChange}
          >
            <MenuItem value="1">January</MenuItem>
            <MenuItem value="2">February</MenuItem>
            <MenuItem value="3">March</MenuItem>
            <MenuItem value="4">April</MenuItem>
            <MenuItem value="5">May</MenuItem>
            <MenuItem value="6">June</MenuItem>
            <MenuItem value="7">July</MenuItem>
            <MenuItem value="8">August</MenuItem>
            <MenuItem value="9">September</MenuItem>
            <MenuItem value="10">October</MenuItem>
            <MenuItem value="11">November</MenuItem>
            <MenuItem value="12">December</MenuItem>
          </Select>
        </FormControl>
        <TextField
          required
          id="start_year"
          type="number"
          name="start_year"
          label="Start Year"
          variant="outlined"
          onChange={handleBreakFormValueChange}
          value={careerBreakValues.start_year ? careerBreakValues.start_year : ''}
          fullWidth
        />
        <FormControlLabel
          control={<Checkbox checked={isPresentRecord} onChange={handleCheckboxChange} />}
          label="I am currently on this break"
          size="small"
        />
        <FormControl>
          <InputLabel id="end-month-label">End Month</InputLabel>
          <Select
            labelId="end-month-label"
            id="end-month"
            name="end_month"
            value={careerBreakValues.end_month}
            disabled={isPresentRecord}
            label="End Month"
            onChange={handleBreakFormValueChange}
          >
            <MenuItem value="1">January</MenuItem>
            <MenuItem value="2">February</MenuItem>
            <MenuItem value="3">March</MenuItem>
            <MenuItem value="4">April</MenuItem>
            <MenuItem value="5">May</MenuItem>
            <MenuItem value="6">June</MenuItem>
            <MenuItem value="7">July</MenuItem>
            <MenuItem value="8">August</MenuItem>
            <MenuItem value="9">September</MenuItem>
            <MenuItem value="10">October</MenuItem>
            <MenuItem value="11">November</MenuItem>
            <MenuItem value="12">December</MenuItem>
          </Select>
        </FormControl>
        <TextField
          id="end_year"
          type="number"
          name="end_year"
          label="End Year"
          variant="outlined"
          disabled={isPresentRecord}
          onChange={handleBreakFormValueChange}
          value={careerBreakValues.end_year ? careerBreakValues.end_year : ''}
          fullWidth
        />
      </OperationModal>
      <OperationModal
        open={isNotYetEmployedModalOpen}
        setIsModalOpen={setIsNotYetEmployedModalOpen}
        onSubmit={updateEmploymentListDisplay}
        canSubmit={canCloseCareerBreakModal(careerBreakValues)}
        formValues={careerBreakValues}
        defaultValues={defaultCareerBreakValues}
        setFormValues={setCareerBreakFormValues}
        operation={doOperation}
      >
        <FormControl required>
          <InputLabel id="reason-label">Reason</InputLabel>
          <Select
            labelId="reason-label"
            id="reason"
            name="reason"
            value={careerBreakValues.reason}
            label="Reason"
            onChange={handleBreakFormValueChange}
          >
            <MenuItem value="advanced-study">Advanced or further study</MenuItem>
            <MenuItem value="exam-prep">Board/Licensure Examination Preparation</MenuItem>
            <MenuItem value="family-related">Family-related reasons</MenuItem>
            <MenuItem value="health-related">Health-related reasons</MenuItem>
            <MenuItem value="apprentice-work">Apprenticeship/Volunteer work</MenuItem>
            <MenuItem value="no-job-opportunity">No job opportunity</MenuItem>
            <MenuItem value="externship">Externship</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        {careerBreakValues.reason == 'advanced-study' && (
          <React.Fragment>
            <TextField
              required
              id="fs_course"
              name="fs_course"
              label="Course"
              variant="outlined"
              onChange={handleBreakFormValueChange}
              value={careerBreakValues.fs_course ? careerBreakValues.fs_course : ''}
              fullWidth
              margin="dense"
            />
            <TextField
              required
              id="fs_school"
              name="fs_school"
              label="School"
              variant="outlined"
              onChange={handleBreakFormValueChange}
              value={careerBreakValues.fs_school ? careerBreakValues.fs_school : ''}
              fullWidth
              margin="dense"
            />
          </React.Fragment>
        )}
        <TextField
          required
          id="location"
          name="location"
          label="Location"
          variant="outlined"
          onChange={handleBreakFormValueChange}
          value={careerBreakValues.location ? careerBreakValues.location : ''}
          fullWidth
        />
        <FormControl required>
          <InputLabel id="start-month-label">Start Month</InputLabel>
          <Select
            labelId="start-month-label"
            id="start-month"
            name="start_month"
            value={careerBreakValues.start_month}
            label="Start Month"
            onChange={handleBreakFormValueChange}
          >
            <MenuItem value="1">January</MenuItem>
            <MenuItem value="2">February</MenuItem>
            <MenuItem value="3">March</MenuItem>
            <MenuItem value="4">April</MenuItem>
            <MenuItem value="5">May</MenuItem>
            <MenuItem value="6">June</MenuItem>
            <MenuItem value="7">July</MenuItem>
            <MenuItem value="8">August</MenuItem>
            <MenuItem value="9">September</MenuItem>
            <MenuItem value="10">October</MenuItem>
            <MenuItem value="11">November</MenuItem>
            <MenuItem value="12">December</MenuItem>
          </Select>
        </FormControl>
        <TextField
          required
          id="start_year"
          type="number"
          name="start_year"
          label="Start Year"
          variant="outlined"
          onChange={handleBreakFormValueChange}
          value={careerBreakValues.start_year ? careerBreakValues.start_year : ''}
          fullWidth
        />
      </OperationModal>
      <DeleteDialog
        open={isDialogOpen}
        handleClose={handleDialogClose}
        handleDelete={handleDeleteItem}
        recordId={deleteValues.id}
        recordDetails={deleteValues}
        loading={isDialogOperationLoading}
      />
      <TextField
        required
        id="months_before_first_job"
        name="months_before_first_job"
        label="Months after graduation before you landed your first job"
        variant="outlined"
        onChange={handleValueChange}
        value={formValues.months_before_first_job ? formValues.months_before_first_job : ''}
        type="number"
        fullWidth
      />
      <Box sx={{ marginTop: '20px' }}>
        <Grid
          container
          justifyContent={'space-between'}
          display={'flex'}
          flexDirection={'row'}
          gap={1}
        >
          <Grid item xs={12} md={3}>
            <Typography variant="formTitle" color={'rgba(0, 0, 0, 0.6)'}>
              Job History
            </Typography>
            <br />
            <Typography variant="smallText" color={'rgba(0, 0, 0, 0.6)'}>
              You may add more entries to your job history (we encourage you to do so) or you may
              skip this section.
            </Typography>
          </Grid>

          <Grid item xs={12} display={'flex'} gap={1}>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={openAddEmploymentItem}
            >
              <Typography variant="smallText">Add job record</Typography>
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={openAddCareerBreakItem}
            >
              <Typography variant="smallText">Add career break</Typography>
            </Button>
            {employmentRecords.length == 0 && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={openNotYetEmployedModal}
              >
                <Typography variant="smallText">{`I don't have a job experience`}</Typography>
              </Button>
            )}
          </Grid>
        </Grid>
        <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
          {employmentRecords &&
            employmentRecords.map((employmentRecord, index) => {
              const dateRange = formatDateRange(
                employmentRecord.start_month,
                employmentRecord.start_year,
                employmentRecord.end_month,
                employmentRecord.end_year
              );
              if (employmentRecord.type == 'employment')
                return (
                  <EditableList
                    key={index}
                    itemId={employmentRecord.id}
                    primaryText={employmentRecord.title}
                    subtitleText={
                      <React.Fragment>
                        {employmentRecord.company
                          .toString()
                          .concat(' · ')
                          .concat(capitalize(employmentRecord.status))}
                        <br />
                        {dateRange}
                        <br />
                        {employmentRecord.is_two_years_after_grad && <em>{'After Graduation'}</em>}
                      </React.Fragment>
                    }
                    addDivider={index < employmentRecords.length - 1}
                    handleEdit={() => openEditEmploymentItem(employmentRecord.id, employmentRecord)}
                    handleDelete={() =>
                      openDeleteEmploymentItem(employmentRecord.id, employmentRecord.title)
                    }
                  />
                );
              if (employmentRecord.type == 'break')
                return (
                  <EditableList
                    key={index}
                    itemId={employmentRecord.id}
                    primaryText={employmentRecord.reason
                      .split('-')
                      .map((w) => w[0].toUpperCase() + w.substring(1).toLowerCase())
                      .join(' ')
                      .concat(' Break')}
                    subtitleText={
                      <React.Fragment>
                        {dateRange}
                        <br />
                        {employmentRecord.is_two_years_after_grad && <em>{'After Graduation'}</em>}
                      </React.Fragment>
                    }
                    addDivider={index < employmentRecords.length - 1}
                    handleEdit={() =>
                      openEditCareerBreakItem(employmentRecord.id, employmentRecord)
                    }
                    handleDelete={() =>
                      openDeleteCareerBreakItem(employmentRecord.id, employmentRecord.reason)
                    }
                  />
                );
            })}
        </List>
      </Box>
      <Box sx={{ marginTop: '20px' }}>
        <FormButtonGroup>
          <Button variant="contained" size="large" color="error" onClick={() => handleScreenBack()}>
            Back
          </Button>
          <Button
            variant="outlined"
            size="large"
            color="primary"
            onClick={onSkip}
            sx={{ marginRight: '10px' }}
          >
            Skip to Finish
          </Button>
          <Box sx={{ m: 0, position: 'relative' }}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              onClick={() => handleNext()}
              disabled={!isShiftable()}
            >
              Next
            </Button>
          </Box>
        </FormButtonGroup>
      </Box>
    </React.Fragment>
  );
}

Employment.propTypes = {
  repo: PropTypes.instanceOf(StudentRepository),
  studentId: PropTypes.string,
  handleScreenNext: PropTypes.func,
  handleScreenBack: PropTypes.func,
  handleErrors: PropTypes.func,
  employmentFormValues: PropTypes.object,
  setEmploymentFormValues: PropTypes.func,
  formValues: PropTypes.object,
  setFormValues: PropTypes.func,
  onSkip: PropTypes.func,
};
